import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { CheckCircle2 } from "lucide-react";
import { useTapToPayStatus } from "../../hooks/useTapToPay";
import { prepareTapToPay, getTTPCapability } from "../../utils/tapToPay";
import { TapToPayGlyph, ttpLabel } from "./TapToPayButton";
import TapToPayEducation from "./TapToPayEducation";

const API = process.env.REACT_APP_BACKEND_URL;

/** One-time in-app awareness moment for Tap to Pay (Apple reqs 3.1–3.4):
 *  full-screen modal shown once per eligible user on this device. */
export function TapToPayAwareness({ kind = "owner" }) {
  const { native, status } = useTapToPayStatus(kind);
  const [open, setOpen] = useState(false);
  const [eduOpen, setEduOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!native || !status) return;
    const relevant = kind === "owner"
      ? status.global_enabled && status.connect_ready
      : status.eligible;
    if (!relevant) return;
    const key = `ttp_aware_v1_${kind}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    setOpen(true);
  }, [native, status, kind]);

  const setUp = () => {
    setOpen(false);
    if (kind === "owner") navigate("/dashboard/settings?tab=stripe-connect");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md max-h-[95vh] overflow-y-auto" data-testid="ttp-awareness-modal">
          <div className="flex flex-col items-center text-center gap-3 pt-4">
            <div className="w-16 h-16 rounded-full bg-sky-500/15 flex items-center justify-center">
              <TapToPayGlyph className="w-9 h-9 text-sky-500" />
            </div>
            <DialogTitle className="text-xl">Accept payments with {ttpLabel()}</DialogTitle>
            <DialogDescription className="text-sm">
              Take contactless cards, Apple Pay and other digital wallets right on this phone — no card reader needed.
            </DialogDescription>
          </div>
          <div className="space-y-2 py-2">
            {[
              "Collect invoice balances and deposits on the spot",
              "Works with contactless cards, phones and watches",
              "Payments are processed securely by Stripe",
            ].map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {t}
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {kind === "owner" && !status?.eligible ? (
              <Button onClick={setUp} className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold" data-testid="ttp-awareness-setup-btn">
                Set up Tap to Pay
              </Button>
            ) : (
              <Button onClick={() => { setOpen(false); setEduOpen(true); }} className="w-full h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold" data-testid="ttp-awareness-learn-btn">
                See how it works
              </Button>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)} className="w-full" data-testid="ttp-awareness-dismiss">
              Not now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TapToPayEducation open={eduOpen} onClose={() => setEduOpen(false)} />
    </>
  );
}

/** Mounted once per layout: warms up the Tap to Pay reader at app open for
 *  enabled merchants (Apple req 1.4), surfaces an unseen failed outcome from
 *  a previous session, and renders the one-time awareness modal. */
export default function TapToPayBoot({ kind = "owner" }) {
  const { native, status } = useTapToPayStatus(kind);

  useEffect(() => {
    if (!native || !status?.eligible || !status.location_id) return;
    // Warm-up is an Apple (iOS) requirement. On Android it would pop the
    // location-permission dialog at app launch with no user context, so the
    // reader connects lazily at user-initiated setup/checkout instead.
    if (getTTPCapability().platform !== "ios") return;
    const tokenEndpoint = kind === "tech"
      ? `${API}/api/tech/terminal/connection-token`
      : `${API}/api/terminal/connection-token`;
    const warm = () => {
      const tok = localStorage.getItem(kind === "tech" ? "tech_token" : "token");
      if (!tok) return;
      prepareTapToPay({
        token_endpoint: tokenEndpoint,
        auth_token: tok,
        location_id: status.location_id,
        connected_account_id: status.connected_account_id,
        merchant_display_name: status.merchant_display_name,
        // Silent background warm-up must NEVER present Apple's T&C sheet — that
        // only happens via the explicit "Set up Tap to Pay" action or at an
        // owner-initiated collection.
        tos_allowed: false,
      });
    };
    warm();
    // Apple req 1.5: also re-warm when the app returns to the foreground.
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - last < 5 * 60 * 1000) return;
      last = Date.now();
      warm();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, status?.eligible, status?.location_id]);

  useEffect(() => {
    if (kind !== "owner" || !native || !status?.business_enabled) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/terminal/transactions?limit=3`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const bad = (res.data.transactions || []).find((t) => {
          const ts = Date.parse(t.updated_at || t.created_at || 0);
          const ageMin = (Date.now() - ts) / 60000;
          if (!Number.isFinite(ageMin) || ageMin > 1440) return false;
          const st = t.payment_status || t.status;
          return st === "failed" || t.status === "failed" || (st === "pending" && ageMin > 5);
        });
        if (bad && localStorage.getItem("ttp_outcome_ack") !== bad.stripe_payment_intent_id) {
          localStorage.setItem("ttp_outcome_ack", bad.stripe_payment_intent_id);
          toast.warning(
            `Your last Tap to Pay attempt (${bad.invoice_number || "invoice"} · $${(bad.amount || 0).toFixed(2)}) was not completed — no charge was made.`,
            { duration: 10000 }
          );
        }
      } catch { /* non-blocking */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [native, status?.business_enabled]);

  return <TapToPayAwareness kind={kind} />;
}
